import * as Sentry from "@sentry/react";

const DSN = process.env.EXPO_PUBLIC_SENTRY_DSN;
const ENV = process.env.EXPO_PUBLIC_SENTRY_ENV ?? "production";

/**
 * Release identifier — must match what `scripts/sentry-upload.mjs`
 * passes to `sentry-cli releases new`. Both sides derive the value
 * from `VERCEL_GIT_COMMIT_SHA.slice(0, 12)` so each deploy's bundle
 * is paired with the right uploaded sourcemap. If the upload script
 * ever changes its derivation, this constant must change in lockstep
 * or stack traces will go back to looking like minified gibberish.
 *
 * EXPO_PUBLIC_SENTRY_RELEASE is an explicit override hatch — set it
 * in Vercel only if you need to pin to a value that doesn't match
 * the commit SHA (e.g. a hotfix tagged with the prior release name).
 * Leave unset in normal operation; the bundler inlines
 * EXPO_PUBLIC_VERCEL_GIT_COMMIT_SHA at build time when Vercel injects
 * VERCEL_GIT_COMMIT_SHA.
 */
const RELEASE =
  process.env.EXPO_PUBLIC_SENTRY_RELEASE ??
  process.env.EXPO_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(0, 12);

let initialized = false;

/**
 * Wire Sentry once on web. Idempotent — safe to call from any layout
 * mount path. SSR (Vercel build phase) and missing-DSN cases are
 * intentional no-ops so dev / preview environments stay quiet without
 * needing a separate stub module.
 */
export function initSentry() {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!DSN) {
    if (ENV !== "production") {
      console.info("[sentry] DSN missing, skipping init");
    }
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Strip auth tokens from breadcrumbs even though they shouldn't be
    // there in the first place — defense in depth.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "fetch" && breadcrumb.data?.url) {
        const headers = (breadcrumb.data as { request_headers?: Record<string, unknown> }).request_headers;
        if (headers) delete headers.Authorization;
      }
      return breadcrumb;
    },
    // Don't capture obviously-not-our-bugs.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      /^Network request failed$/,
    ],
  });
  initialized = true;
}

/**
 * Tag every subsequent Sentry event with the affected user. Privacy
 * boundary: ONLY id, email, role — never SSN, address, or any other
 * sensitive PII. Pass null on sign-out to clear the user.
 */
export function setSentryUser(
  user: { id: string; email?: string; role?: string } | null
): void {
  if (!initialized) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
    email: user.email,
    ...(user.role ? { role: user.role } : {}),
  });
}

/**
 * Wraps `Sentry.captureException` with a fallback that logs to console
 * when Sentry isn't initialized. The `[sentry-not-init]` prefix makes
 * it greppable in browser devtools so we can spot which paths fired
 * before the Sentry init landed (or in dev, where DSN is intentionally
 * missing).
 */
export function captureSentryException(
  err: unknown,
  context?: Record<string, unknown>
): void {
  if (!initialized) {
    console.error("[sentry-not-init]", err, context);
    return;
  }
  Sentry.captureException(err, { extra: context });
}
